import React, { CSSProperties, useEffect, useState } from "react";
import { ORViewer } from "./orviewer";
import {
    setCurrentScene,
    setCurrentSceneKey,
    setSceneConfig,
    setSceneName,
} from "state/slices/SceneSlice";
import { LoaderService } from "Classes/LoaderService";
import { useTypedDispatch, useTypedSelector } from "hooks/store";
import { ISceneConfiguration } from "Classes/interfaces";
import { useLocation } from "react-router-dom";
import { useCmrAuth } from "state/CMRAuth";
import { openNotification } from "state/slices/NotificationSlice";
import { useHistory } from "react-router-dom";
import RoutesName from "routes/routesName";

function getStyle(): CSSProperties {
    return {
        width: "100%",
        height: "100vh",
        overflow: "hidden",
        cursor: "pointer",
    };
}

interface Props {
    sceneKey?: string;
}

type SceneResponse = {
    data: ISceneConfiguration;
    thumbnail?: string;
    id: string;
    user: string;
    global: boolean;
    shared: boolean;
    sceneName: string;
    displayName: string;
    surgeon: string;
    hospital: string;
    country: string;
    categoryId: number;
};

const ORViewerWrapper = ({ sceneKey }: Props): JSX.Element => {
    const dispatch = useTypedDispatch();
    const currentScene = useTypedSelector((store) => store.Scene.currentScene);
    const [isLoaded, setIsLoaded] = useState(false);
    const [sceneMeta, setSceneMeta] = useState<SceneResponse | null>(null);
    const cmrAuth = useCmrAuth();
    const router = useHistory();

    const location = useLocation<{ isGlobal?: boolean; newScene?: boolean }>();

    useEffect(() => {
        const loadScene = async () => {
            let loadedScene: ISceneConfiguration | null = null;

            if (location.state?.newScene) {
                loadedScene = currentScene ?? LoaderService.loadSceneConfiguration("New");

                dispatch(
                    setSceneConfig({
                        global: false,
                        shared: false,
                        user: cmrAuth.user?.cognitoSub ?? "",
                        displayName: cmrAuth.user?.name ?? "",
                        surgeon: "",
                        hospital: "",
                        country: "",
                        categoryId: 0,
                        isOwner: true,
                    })
                );

                dispatch(setCurrentScene(loadedScene));
                setIsLoaded(true);

                return;
            }

            if (!sceneKey) {
                dispatch(
                    openNotification({
                        isNotificationOpen: true,
                        type: "error",
                        text: "Setup could not be loaded.",
                    })
                );

                router.push({ pathname: RoutesName.preset });

                return;
            }

            const token = await cmrAuth.getAccessToken();

            const endPoint = location.state?.isGlobal
                ? `${process.env.REACT_APP_API_URL}/api/global/${sceneKey}`
                : `${process.env.REACT_APP_API_URL}/api/scene/${sceneKey}`;

            const res = await fetch(endPoint, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            const response: SceneResponse = await res.json();

            const {
                global,
                shared,
                user,
                displayName,
                surgeon,
                hospital,
                country,
                categoryId,
                sceneName,
            } = response;

            setSceneMeta(response);

            if (response?.data) {
                loadedScene = response.data;
            }

            dispatch(setCurrentSceneKey(sceneKey));

            if (!loadedScene && currentScene) {
                loadedScene = currentScene;
            }

            if (!loadedScene) {
                loadedScene = LoaderService.loadSceneConfiguration("New");
            }

            dispatch(setSceneName(sceneName));

            const isOwner = user === cmrAuth.user?.cognitoSub;

            dispatch(
                setSceneConfig({
                    global,
                    shared,
                    user,
                    displayName,
                    surgeon,
                    hospital,
                    country,
                    categoryId,
                    isOwner,
                })
            );

            dispatch(setCurrentScene(loadedScene));
            setIsLoaded(true);
        };

        loadScene();
    }, [sceneKey]);

    const hasThumbnail = !!sceneMeta?.thumbnail;

    return (
        <div role={"cmrWrapper"} style={getStyle()}>
            {isLoaded && (
                <ORViewer scene={currentScene} sceneKey={sceneKey} hasThumbnail={hasThumbnail} />
            )}
        </div>
    );
};

export default ORViewerWrapper;
